import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import {
    ResourcePushWeekTemplateHomework, ResourcePushWeekTemplateHomeworkLessonSubTypeItems,
    ResourcePushWeekTemplateHomeworkLessonSubTypes,
    ResourcePushWeekTemplateHomeworkLessonSubTypeValues,
    ResourcePushWeekTemplateHomeworkResource,
    ResourcePushWeekTemplateHomeworkTypes,
    ResourcePushWeekTemplateHomeworkTypeValues,
    ResourcePushWeekTemplateLevel3Resource,
} from 'src/app/model/ResourcePushTemplate';
import {SyllabusService} from '../../../../../../services/syllabus.service';
import { trigger, state, style, animate, transition } from '@angular/animations';

@Component({
    selector: 'app-admin-resource-category-template-homework',
    templateUrl: './homework.component.html',
    animations: [
        trigger('homeworkAnimation', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'translateY(-20px)'
                }),
                animate('200ms ease-out', style({
                    opacity: 1,
                    transform: 'translateY(0)'
                }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({
                    opacity: 0,
                    transform: 'translateY(20px)'
                }))
            ])
        ])
    ],
    standalone: false
})
export class AdminResourceCategoryTemplateHomeworkComponent implements OnInit {
    @Input() index!: number;
    @Input() homework!: ResourcePushWeekTemplateHomework;
    @Output() homeworkChange = new EventEmitter<ResourcePushWeekTemplateHomework>();

    level1Resources: ResourcePushWeekTemplateHomeworkResource[] = [];
    level2Resources: ResourcePushWeekTemplateHomeworkResource[] = [];
    level3Resources: ResourcePushWeekTemplateHomeworkResource[] = [];
    level3ResourcesMap: Record<number, ResourcePushWeekTemplateHomeworkResource[]> = {};
    level4ResourcesMap: Record<string, ResourcePushWeekTemplateHomeworkResource[]> = {};

    // 资源树相关属性
    resourceTreeLevels: Array<{
        level: number;
        name: string;
        options: ResourcePushWeekTemplateHomeworkResource[];
        selectedValue: number | null;
    }> = [];

    @Output() deleteHomework = new EventEmitter<void>();
    @Output() addHomework = new EventEmitter<void>();

    form!: FormGroup;
    loading = true;

    protected readonly ResourcePushWeekTemplateHomeworkTypes = ResourcePushWeekTemplateHomeworkTypes;
    protected readonly ResourcePushWeekTemplateHomeworkLessonSubTypes = ResourcePushWeekTemplateHomeworkLessonSubTypes;
    protected readonly ResourcePushWeekTemplateHomeworkTypeValues = ResourcePushWeekTemplateHomeworkTypeValues;
    protected readonly ResourcePushWeekTemplateHomeworkLessonSubTypeItems = ResourcePushWeekTemplateHomeworkLessonSubTypeItems;

    constructor(
        private fb: FormBuilder,
        private syllabusService: SyllabusService,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        this.initForm();
        this.setupFormListeners();
        const { type, level1ResourceId, level2ResourceId, level3Resources } = this.homework;
        const tasks: Array<Promise<any> | undefined | null | number> = [
            type && this.updateLevel1Resources(type),
            level1ResourceId && this.updateLevel2Resources(level1ResourceId),
            level2ResourceId && this.updateLevel3Resources(),
        ];

        // 注意：音视频类型的资源树初始化在 updateLevel3Resources 中处理

        for (const level3Resource of level3Resources || []) {
            switch (type) {
                case ResourcePushWeekTemplateHomeworkTypeValues.CARTOON:
                    tasks.push(this.updateCartoonLevel4Resources(level3Resource.level3ResourceId, level3Resource.level3ResourceType));
                    break;
                case ResourcePushWeekTemplateHomeworkTypeValues.EXERCISE:
                    tasks.push(this.updateExerciseLevel4Resources(level3Resource.level3ResourceId));
                    break;
                case ResourcePushWeekTemplateHomeworkTypeValues.BOOK:
                    // tslint:disable-next-line:no-shadowed-variable
                    for (const level3Resource of this.homework.level3Resources || []) {
                        tasks.push(this.updateBookLevel3Resources(level2ResourceId, level3Resource.level3ResourceType));
                    }
                    tasks.push(this.updateBookLevel4Resources(level3Resource.level3ResourceId, level3Resource.level3ResourceType));
                    break;
                default:
                    break;
            }
        }

        await Promise.all(tasks.filter(Boolean));
        this.loading = false;
    }

    private initForm() {
        this.form = this.fb.group({
            type: [this.homework.type || null, [Validators.required]],
            level1ResourceId: [this.homework.level1ResourceId || null, [Validators.required]],
            level1ResourceName: [this.homework.level1ResourceName || ''],
            level2ResourceId: [this.homework.level2ResourceId || null, [Validators.required]],
            level2ResourceName: [this.homework.level2ResourceName || ''],
            level3ResourceIds: [this.homework.level3ResourceIds || [], [
                () => this.isLevel3ResourceIds() ? Validators.minLength(1) : null,
            ]],
            level3Resources: this.fb.array(
                this.homework.level3Resources?.map(
                    level3Resource => this.createLevel3ResourceForm(level3Resource, this.homework.type)) || [],
                {
                    validators: [() => this.isLevel3ResourceArray() ? Validators.minLength(1) : null]
                }
            )
        });

        this.form.get('type')?.valueChanges.subscribe(() => this.updateLevel3ResourceTypeValidators());
    }

    private updateLevel3ResourceTypeValidators() {
        const type = this.form.get('type')?.value;
        const _updateLevel3ResourceTypeValidators = (validators: ValidatorFn[]) => {
            this.form.get('level3Resources')?.get('level3ResourceType')?.setValidators(validators);
        };
        switch (type) {
            case ResourcePushWeekTemplateHomeworkTypeValues.CARTOON:
                _updateLevel3ResourceTypeValidators([Validators.required]);
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.EXERCISE:
                _updateLevel3ResourceTypeValidators([]);
                break;
        }
    }

    private naturalSort<T extends { name: string }>(array: Array<T>): Array<T> {
        return array;
        // maybe not need resort the order
        // const collator = new Intl.Collator('zh', {
        //     numeric: true,
        //     sensitivity: 'base'
        // });
        // return array.sort((a, b) => collator.compare(a.name, b.name));
    }

    private createLevel3ResourceForm(level3Resource?: ResourcePushWeekTemplateLevel3Resource, type?: string | null) {
        if (!level3Resource) {
            level3Resource = {
                level3ResourceId: null,
                level3ResourceType: null,
                level4ResourceIds: [],
            };
        }

        const level3ResourceForm = this.fb.group({
            level3ResourceId: [level3Resource.level3ResourceId || null, [Validators.required]],
            level3ResourceType: [
                level3Resource.level3ResourceType || null,
                type === ResourcePushWeekTemplateHomeworkTypeValues.CARTOON ? [Validators.required] : [],
            ],
            level4ResourceIds: [level3Resource.level4ResourceIds || [], [Validators.required, Validators.minLength(1)]]
        });

        if (type) {
            this.setupLevel3ResourceFormListeners(type, level3ResourceForm);
        }

        return level3ResourceForm;
    }


    private async updateLevel1Resources(type?: string) {
        if (!type) {
            this.level1Resources = [];
            this.level2Resources = [];
            return;
        }
        if (type === ResourcePushWeekTemplateHomeworkTypeValues.DUBBING) {
            const opts = await this.syllabusService.getDubbingOptions();
            this.level1Resources = opts.difficulty;
            this.level2Resources = opts.category;
            this.cdr.detectChanges();
            return;
        }
        this.level1Resources = await this.syllabusService.getResources(type, 0);
    }

    private async updateLevel2Resources(level1ResourceId?: number) {
        const type = this.form.get('type')!.value;
        if (type === ResourcePushWeekTemplateHomeworkTypeValues.DUBBING) {
            return;
        }
        if (!level1ResourceId || !type) {
            this.level2Resources = [];
            return;
        }
        this.level2Resources = await this.syllabusService.getResources(type, level1ResourceId);
    }

    private async updateLevel3Resources() {
        const type = this.form.get('type')!.value;
        const level1ResourceId = this.form.get('level1ResourceId')!.value;
        const level2ResourceId = this.form.get('level2ResourceId')!.value;
        if (!level1ResourceId || !level2ResourceId) {
            this.level3Resources = [];
            return;
        }
        switch (type) {
            case ResourcePushWeekTemplateHomeworkTypeValues.CARTOON:
                this.level3Resources = this.naturalSort(await this.syllabusService.getCartoons(level2ResourceId));
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.VIDEO:
                // 对于音视频类型，使用资源树来筛选
                await this.initializeResourceTreeForAudioVideo(level2ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.AUDIO:
                // 对于音视频类型，使用资源树来筛选
                await this.initializeResourceTreeForAudioVideo(level2ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.EXERCISE:
                this.level3Resources = this.naturalSort(await this.syllabusService.getExercises(level2ResourceId));
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.DUBBING:
                this.level3Resources = this.naturalSort(await this.syllabusService.getDubbingItems(level1ResourceId, level2ResourceId));
                break;
            default:
                break;
        }
    }

    // 新增：为音视频类型初始化资源树
    private async initializeResourceTreeForAudioVideo(level2ResourceId: number) {
        const type = this.form.get('type')!.value;
        console.log('开始初始化音视频资源树:', { type, level2ResourceId });
        
        try {
            // 使用 getResources 接口获取第一级资源树
            const firstLevelResources = await this.syllabusService.getResources(type, level2ResourceId);
            console.log('获取到的第一级资源:', firstLevelResources);
            
            // 初始化资源树级别
            this.initializeResourceTreeLevels(firstLevelResources);
            
            // 无论是否有资源树，都要获取课程列表
            await this.updateLevel3ResourcesFromTree();
        } catch (error) {
            console.error('获取资源树失败:', error);
            this.resourceTreeLevels = [];
            // 出错时也尝试直接获取资源
            await this.updateLevel3ResourcesFromTree();
        }
    }

    // 新增：初始化资源树级别
    private initializeResourceTreeLevels(firstLevelResources: ResourcePushWeekTemplateHomeworkResource[]) {
        console.log('初始化资源树级别:', {
            firstLevelResources,
            currentResourceTreeLevels: this.resourceTreeLevels
        });
        
        this.resourceTreeLevels = [];
        
        // 第一级：3级目录（因为前面已经有1级和2级了）
        if (firstLevelResources.length > 0) {
            const firstLevel = {
                level: 1,
                name: '3级目录',
                options: firstLevelResources,
                selectedValue: null
            };
            
            console.log('添加第一级:', firstLevel);
            this.resourceTreeLevels.push(firstLevel);
        }
        
        console.log('初始化后的资源树级别:', this.resourceTreeLevels);
    }

    // 新增：处理资源树级别选择变化
    async handleResourceTreeLevelChange(levelIndex: number, selectedValue: number | null) {
        console.log('=== 资源树级别选择变化开始 ===');
        console.log('参数:', { levelIndex, selectedValue });
        console.log('变化前的资源树级别:', JSON.parse(JSON.stringify(this.resourceTreeLevels)));
        
        // 更新当前级别的选择
        this.resourceTreeLevels[levelIndex].selectedValue = selectedValue;
        
        // 清除后续级别的选择
        for (let i = levelIndex + 1; i < this.resourceTreeLevels.length; i++) {
            this.resourceTreeLevels[i].selectedValue = null;
        }
        
        // 移除后续级别
        this.resourceTreeLevels.splice(levelIndex + 1);
        
        console.log('更新后的资源树级别:', JSON.parse(JSON.stringify(this.resourceTreeLevels)));
        
        // 强制触发变更检测，确保资源树级别更新
        this.cdr.detectChanges();
        
        console.log('开始更新课程列表...');
        console.log('当前选择的级别:', this.resourceTreeLevels.filter(level => level.selectedValue !== null));
        // 每次选择后都立即更新 level3Resources
        await this.updateLevel3ResourcesFromTree();
        console.log('课程列表更新完成');
        
        // 如果选择了值，添加下一级
        if (selectedValue) {
            console.log('开始添加下一级资源树...');
            await this.addNextResourceTreeLevel(levelIndex, selectedValue);
            console.log('下一级资源树添加完成');
        }
        
        console.log('=== 资源树级别选择变化结束 ===');
    }

    // 新增：添加下一级资源树
    private async addNextResourceTreeLevel(levelIndex: number, parentId: number) {
        const type = this.form.get('type')!.value;
        console.log('添加下一级资源树:', { levelIndex, parentId, type });
        
        try {
            // 使用 getResources 接口获取下一级资源
            const nextLevelResources = await this.syllabusService.getResources(type, parentId);
            console.log('获取到的下一级资源:', nextLevelResources);
            
            if (nextLevelResources.length > 0) {
                // 计算正确的级别名称：当前级别 + 1
                const nextLevelNumber = levelIndex + 4; // 因为第一级是3级目录，所以下一级是4级目录
                const nextLevel = {
                    level: levelIndex + 2,
                    name: `${nextLevelNumber}级目录`,
                    options: nextLevelResources,
                    selectedValue: null
                };
                
                console.log('添加新级别:', nextLevel);
                this.resourceTreeLevels.push(nextLevel);
                
                // 强制触发变更检测，确保新级别显示
                this.cdr.detectChanges();
            } else {
                console.log('没有下一级资源，不添加新级别');
            }
        } catch (error) {
            console.error('获取下一级资源失败:', error);
        }
    }

    // 新增：根据资源树更新 level3Resources
    private async updateLevel3ResourcesFromTree() {
        const type = this.form.get('type')!.value;
        const level2ResourceId = this.form.get('level2ResourceId')!.value;
        
        console.log('开始更新课程列表 - 方法入口:', {
            type,
            level2ResourceId,
            resourceTreeLevels: this.resourceTreeLevels
        });
        
        if (!level2ResourceId) {
            console.log('没有 level2ResourceId，清空课程列表');
            this.level3Resources = [];
            this.cdr.detectChanges();
            return;
        }

        // 获取最后选择的资源树节点ID，如果没有选择任何级别，则使用 level2ResourceId
        const lastSelectedLevel = this.resourceTreeLevels
            .filter(level => level.selectedValue !== null)
            .pop(); // 获取最后一个有选择值的级别
        const finalResourceId = lastSelectedLevel ? lastSelectedLevel.selectedValue : level2ResourceId;

        console.log('更新课程列表:', {
            type,
            level2ResourceId,
            finalResourceId,
            resourceTreeLevels: this.resourceTreeLevels,
            lastSelectedLevel,
            selectedLevels: this.resourceTreeLevels.filter(level => level.selectedValue !== null)
        });

        try {
            let result;
            switch (type) {
                case ResourcePushWeekTemplateHomeworkTypeValues.VIDEO:
                    result = await this.syllabusService.getVideos(finalResourceId);
                    console.log('获取视频结果:', result);
                    this.level3Resources = this.naturalSort(result);
                    break;
                case ResourcePushWeekTemplateHomeworkTypeValues.AUDIO:
                    result = await this.syllabusService.getAudios(finalResourceId);
                    console.log('获取音频结果:', result);
                    this.level3Resources = this.naturalSort(result);
                    break;
                default:
                    this.level3Resources = [];
                    break;
            }
            
            console.log('课程列表更新完成:', this.level3Resources);
            
            // 强制触发变更检测
            this.cdr.detectChanges();
        } catch (error) {
            console.error('获取资源列表失败:', error);
            this.level3Resources = [];
            this.cdr.detectChanges();
        }
    }

    private setupFormListeners() {
        // 监听表单变化并更新父组件
        this.form.valueChanges.subscribe(value => {
            this.homeworkChange.emit(Object.assign(this.homework, value));
        });

        // 监听类型变化
        this.form.get('type')!.valueChanges.subscribe(async type => {
            const level1ResourceIdControl = this.form.get('level1ResourceId')!;
            const level2ResourceIdControl = this.form.get('level2ResourceId')!;
            level1ResourceIdControl.setValue(null);
            level2ResourceIdControl.setValue(null);
            this.form.get('level3ResourceIds')!.setValue([]);
            this.getLevel3ResourcesForm().clear();
            this.resourceTreeLevels = [];
            await this.updateLevel1Resources(type);
        });

        // 监听教材/难度变化
        this.form.get('level1ResourceId')!.valueChanges.subscribe(async level1ResourceId => {
            const type = this.form.get('type')!.value;
            if (type === ResourcePushWeekTemplateHomeworkTypeValues.DUBBING) {
                this.form.get('level3ResourceIds')!.setValue([]);
                await this.updateLevel3Resources();
                return;
            }
            const level2ResourceIdControl = this.form.get('level2ResourceId')!;
            level2ResourceIdControl.setValue(null);
            this.resourceTreeLevels = [];
            await this.updateLevel2Resources(level1ResourceId);
        });

        // 监听教材级别/分类变化
        this.form.get('level2ResourceId')!.valueChanges.subscribe(async () => {
            this.form.get('level3ResourceIds')!.setValue([]);
            this.getLevel3ResourcesForm().clear();
            this.resourceTreeLevels = [];
            await this.updateLevel3Resources();
        });
    }

    getLevel3ResourcesForm(): FormArray {
        const control = this.form.get('level3Resources');
        if (!(control instanceof FormArray)) {
            throw new Error('level3Resources is not a FormArray');
        }
        return control;
    }

    isLevel3ResourceIds(): boolean {
        const type = this.form?.get('type')?.value;
        return type ? [
            ResourcePushWeekTemplateHomeworkTypeValues.VIDEO,
            ResourcePushWeekTemplateHomeworkTypeValues.AUDIO,
            ResourcePushWeekTemplateHomeworkTypeValues.DUBBING,
        ].includes(type) : false;
    }

    isLevel3ResourceArray(): boolean {
        const type = this.form?.get('type')?.value;
        return type
            ? [
                ResourcePushWeekTemplateHomeworkTypeValues.CARTOON,
                ResourcePushWeekTemplateHomeworkTypeValues.EXERCISE,
                ResourcePushWeekTemplateHomeworkTypeValues.BOOK,
            ].includes(type)
            : false;
    }

    // 新增：判断是否显示资源树
    isShowResourceTree(): boolean {
        const type = this.form?.get('type')?.value;
        return type ? [
            ResourcePushWeekTemplateHomeworkTypeValues.VIDEO,
            ResourcePushWeekTemplateHomeworkTypeValues.AUDIO,
        ].includes(type) : false;
    }

    getValidateStatus(control: AbstractControl | null): string {
        if (!control) { return ''; }
        return control.touched ? (control.invalid ? 'error' : 'success') : '';
    }

    isDubbing(): boolean {
        return this.form?.get('type')?.value === ResourcePushWeekTemplateHomeworkTypeValues.DUBBING;
    }

    getLevel1Label(): string {
        return this.isDubbing() ? '难度' : '教材名称';
    }

    getLevel2Label(): string {
        return this.isDubbing() ? '类型' : '教材级别';
    }

    getLevel3Label(): string {
        return this.isDubbing() ? '选择片段' : '选择课程';
    }

    getLevel3Resources(level3ResourceControl: AbstractControl): ResourcePushWeekTemplateHomeworkResource[] {
        const type = this.form.get('type')?.value;  
        if (type === ResourcePushWeekTemplateHomeworkTypeValues.BOOK) {
            const level3ResourceType = level3ResourceControl.get('level3ResourceType')?.value;
            return this.level3ResourcesMap[level3ResourceType] || [];
        }
        return this.level3Resources;
    }

    getLevel4Resources(level3ResourceControl: AbstractControl): ResourcePushWeekTemplateHomeworkResource[] {
        const level3ResourceId = level3ResourceControl.get('level3ResourceId')?.value;
        const level3ResourceType = level3ResourceControl.get('level3ResourceType')?.value;
        if (level3ResourceId === undefined || level3ResourceId === null) {
            return [];
        }
        const key = [level3ResourceId, level3ResourceType].filter(Boolean).join(':');
        return this.level4ResourcesMap[key] || [];
    }

    handleAddLevel3ResourceAdd() {
        const type = this.form.get('type')?.value;
        const level3ResourceForm = this.createLevel3ResourceForm(undefined, type);
        this.getLevel3ResourcesForm().push(level3ResourceForm);
    }

    private setupLevel3ResourceFormListeners(type: string, level3ResourceForm: FormGroup) {
        switch (type) {
            case ResourcePushWeekTemplateHomeworkTypeValues.CARTOON:
                const _updateCartoonLevel4Resources = async () => {
                    const level3ResourceId = level3ResourceForm.get('level3ResourceId')?.value;
                    const level3ResourceType = level3ResourceForm.get('level3ResourceType')?.value;
                    console.log('level3ResourceId', level3ResourceId);
                    console.log('level3ResourceType', level3ResourceType);
                    level3ResourceForm.get('level4ResourceIds')?.setValue([]);
                    await this.updateCartoonLevel4Resources(level3ResourceId, level3ResourceType);
                };

                level3ResourceForm.get('level3ResourceType')?.valueChanges.subscribe(_updateCartoonLevel4Resources);
                level3ResourceForm.get('level3ResourceId')?.valueChanges.subscribe(_updateCartoonLevel4Resources);
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.EXERCISE:
                const _updateExerciseLevel4Resources = async () => {
                    const level3ResourceId = level3ResourceForm.get('level3ResourceId')?.value;
                    level3ResourceForm.get('level4ResourceIds')?.setValue([]);
                    await this.updateExerciseLevel4Resources(level3ResourceId);
                };
                level3ResourceForm.get('level3ResourceId')?.valueChanges.subscribe(_updateExerciseLevel4Resources);
                break;
            case ResourcePushWeekTemplateHomeworkTypeValues.BOOK:
                const _updateBookLevel3Resources = async () => {
                    console.log('updateBookLevel3Resources');
                    const level2ResourceId = this.form.get('level2ResourceId')?.value;
                    const level3ResourceType = level3ResourceForm.get('level3ResourceType')?.value;
                    level3ResourceForm.get('level4ResourceIds')?.setValue([]);
                    level3ResourceForm.get('level3ResourceId')?.setValue(null);
                    await this.updateBookLevel3Resources(level2ResourceId, level3ResourceType);
                };
                level3ResourceForm.get('level3ResourceType')?.valueChanges.subscribe(_updateBookLevel3Resources);
                const _updateBookLevel4Resources = async () => {
                    const level3ResourceId = level3ResourceForm.get('level3ResourceId')?.value;
                    const level3ResourceType = level3ResourceForm.get('level3ResourceType')?.value;
                    level3ResourceForm.get('level4ResourceIds')?.setValue([]);
                    if (level3ResourceId) {
                        if (level3ResourceType) {
                            await this.updateBookLevel4Resources(level3ResourceId, level3ResourceType);
                        }
                    }
                };
                level3ResourceForm.get('level3ResourceId')?.valueChanges.subscribe(_updateBookLevel4Resources);
                break;
            default:
                break;
        }
    }

    private async updateBookLevel3Resources(level2ResourceId?: number | null, level3ResourceType?: number | null) {
        if (!level2ResourceId || !level3ResourceType) {
            return;
        }
        this.level3Resources = this.naturalSort(await this.syllabusService.getBooks(level2ResourceId, level3ResourceType));
        this.level3ResourcesMap[level3ResourceType] = this.level3Resources;
    }

    private async updateBookLevel4Resources(level3ResourceId?: number | null, level3ResourceType?: number | null) {
        if (!level3ResourceId || !level3ResourceType) {
            return;
        }

        const key = [level3ResourceId, level3ResourceType].filter(Boolean).join(':');
        if (this.level4ResourcesMap[key]) {
            return;
        }

        let resources: Array<ResourcePushWeekTemplateHomeworkResource> = [];
        switch (level3ResourceType) {
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.CARTOON:
                resources = await this.syllabusService.getBookCoursorwareResourceReadings(level3ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.RECORD_AUDIO:
                resources = await this.syllabusService.getBookCoursorwareResourceRecordAudios(level3ResourceId);
                break;
            default:
                break;
        }

        this.level4ResourcesMap[key] = this.naturalSort(resources);
    }

    private async updateExerciseLevel4Resources(level3ResourceId?: number | null) {
        if (!level3ResourceId) {
            return;
        }
        this.level4ResourcesMap[level3ResourceId] = this.naturalSort(await this.syllabusService.getExerciseItems(level3ResourceId));
    }

    private async updateCartoonLevel4Resources(level3ResourceId?: number | null, level3ResourceType?: number | null) {
        if (!level3ResourceId) {
            return;
        }

        const key = [level3ResourceId, level3ResourceType].filter(Boolean).join(':');
        if (this.level4ResourcesMap[key]) {
            return;
        }

        let resources: ResourcePushWeekTemplateHomeworkResource[] = [];
        switch (level3ResourceType) {
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.CARTOON:
                resources = await this.syllabusService.getCoursewareResourceCartoons(level3ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.VIDEO:
                resources = await this.syllabusService.getCoursewareResourceVideos(level3ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.AUDIO:
                resources = await this.syllabusService.getCoursewareResourceAudios(level3ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.EXERCISE:
                resources = await this.syllabusService.getCoursewareResourceExercises(level3ResourceId);
                break;
            case ResourcePushWeekTemplateHomeworkLessonSubTypeValues.RECORD_AUDIO:
                resources = await this.syllabusService.getCoursewareResourceRecordAudios(level3ResourceId);
                break;
            default:
                break;
        }
        this.level4ResourcesMap[key] = this.naturalSort(resources);
    }

    handleDeleteLevel3Resource(level3ResourceIndex: number) {
        this.getLevel3ResourcesForm().removeAt(level3ResourceIndex);
    }

    handleDeleteHomework() {
        this.deleteHomework.emit();
    }

    handleAddHomework() {
        this.addHomework.emit();
    }

    validate() {
        this.form.markAllAsTouched();
        return this.form.valid;
    }

}
